import { HttpErrorResponse, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { ModalController, NavController } from '@ionic/angular/lazy';
import { TranslateService } from '@ngx-translate/core';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AlertService } from '../services/alert.service';
import { AuthService } from '../services/auth.service';

// Server returns 400 with the validation error keyed by this header name when a
// spam-prevention token has timed out. The message itself is localized, so match the key.
const SPAM_TOKEN_ERROR_KEY = 'x-spam-prevention-token';

// Shared across interceptor invocations so parallel failing requests only redirect once.
let handlingSpamTokenExpiry = false;

const isSpamTokenExpiredError = (error: HttpErrorResponse): boolean => {
  const errors = error.status === 400 ? error.error?.errors : null;
  if (!errors || typeof errors !== 'object') {
    return false;
  }
  return Object.keys(errors).some((key) => key.toLowerCase() === SPAM_TOKEN_ERROR_KEY);
};

export const AuthInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
) => {
  if (!req.url.startsWith(environment.API_ROOT)) {
    return next(req);
  }

  const authService = inject(AuthService);
  const alertService = inject(AlertService);
  const translateService = inject(TranslateService);
  const modalCtrl = inject(ModalController);
  const navCtrl = inject(NavController);

  const handleSpamTokenExpired = async (message: string) => {
    if (handlingSpamTokenExpiry) {
      return;
    }
    handlingSpamTokenExpiry = true;
    try {
      alertService.errorEvent(message);
      while (await modalCtrl.getTop()) {
        await modalCtrl.dismiss();
      }
      await navCtrl.navigateRoot(['/orders'], { replaceUrl: true });
    } finally {
      handlingSpamTokenExpiry = false;
    }
  };

  const processErrorMessages = (error: any) => {
    const errorMessages: Array<string> = [];
    if (error.error) {
      if (!!error.error.errors) {
        Object.keys(error.error.errors).forEach((key) => {
          errorMessages.push(error.error.errors[key]);
        });
      } else if (error.statusText === 'Unknown Error' && error.status === 0) {
        errorMessages.push(
          'Too Many Requests. Please try again later - Trop de requêtes. Veuillez réessayer plus tard'
        );
      } else {
        errorMessages.push(
          error.error.message ??
            "Something went wrong. Please try again - Une erreur s'est produite. Veuillez réessayer"
        );
      }
    }
    return { message: errorMessages.join(' / ') };
  };

  const handleHttpError = (error: any) => {
    if (error instanceof HttpErrorResponse) {
      if (isSpamTokenExpiredError(error)) {
        const message = translateService.instant('App.Errors.SpamTokenExpired');
        handleSpamTokenExpired(message);
        return throwError(() => ({ message, spamTokenExpired: true }));
      }
      if (error.status === 401 || error.status === 403) {
        authService.logout(true);
      } else if (error.status === 400 || error.status === 404 || error.status === 0) {
        return throwError(() => processErrorMessages(error));
      }
    }
    return throwError(() => error);
  };

  return from(authService.acquireToken()).pipe(
    switchMap((token) => {
      const outReq = token
        ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
        : req;
      return next(outReq).pipe(
        catchError((error: HttpErrorResponse) => handleHttpError(error)),
      );
    }),
  );
};
